import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Layout, Monitor, Gauge, Brain, CreditCard, Smartphone, ArrowRight, ArrowLeft, ShieldCheck, Heart, X, MessageCircle, ChevronRight, CheckCircle, PenTool, Database, Shield, Zap, Megaphone, Terminal, Cpu, Globe, BarChart3, PieChart, TrendingUp, Settings, FileCheck, RefreshCw, Activity, Bell, FileText, Mic } from 'lucide-react';

import PoweredByLuk from '../LukIA/PoweredByLuk';

const services = [
    {
        id: 'erp',
        name: 'Lukos ERP',
        description: 'Gestão completa para postos e conveniências, integrando financeiro e fiscal.',
        image: '/images/Serviceerp.png',
        icon: Layout,
        color: 'text-[#7c3aed]',
        features: [
            {
                title: 'Estoque Real-Time',
                description: 'Controle de entrada e saída de combustíveis com atualização instantânea.',
                icon: Database
            },
            {
                title: 'Gestão Financeira',
                description: 'Contas a pagar, receber e fluxo de caixa integrado ao checkout.',
                icon: CreditCard
            },
            {
                title: 'Fiscal Automático',
                description: 'Emissão de NF-e e NFC-e com regras tributárias pré-configuradas.',
                icon: FileCheck
            },
            {
                title: 'Relatórios Master',
                description: 'BI completo com KPIs operacionais e financeiros detalhados.',
                icon: BarChart3
            }
        ],
        specs: [
            { label: 'Plataforma', value: 'Windows / Web Cloud' },
            { label: 'Banco de Dados', value: 'SQL Server / PostgreSQL' },
            { label: 'Integração Hub', value: 'API Restful' },
            { label: 'Suporte', value: '24/7 Remoto / Presencial' }
        ]
    },
    {
        id: 'pdv',
        name: 'PDV PISTA',
        description: 'Frente de caixa ultra-rápido com integração total de pagamentos e estoque.',
        image: '/images/pdv_elite_bg.png',
        icon: Monitor,
        color: 'text-[#7c3aed]',
        features: [
            {
                title: 'Interface Ultra-Fast',
                description: 'Processamento de vendas em milissegundos para evitar filas.',
                icon: Zap
            },
            {
                title: 'Offline First',
                description: 'Continue vendendo mesmo sem internet. Sincronização automática.',
                icon: ShieldCheck
            },
            {
                title: 'Pagamento Integrado',
                description: 'Pix, cartões e frotas em uma única interface única.',
                icon: CreditCard
            },
            {
                title: 'Sincronização ERP',
                description: 'Baixa de estoque e registro financeiro imediato após a venda.',
                icon: RefreshCw
            }
        ],
        specs: [
            { label: 'Tempo de Resposta', value: '< 100ms' },
            { label: 'Protocolos POS', value: 'TEF / Smart POS' },
            { label: 'Hardware', value: 'Touch-Screen Optimized' },
            { label: 'Backup', value: 'Local + Nuvem' }
        ]
    },
    {
        id: 'fuel',
        name: 'Fuel Check',
        description: 'Monitoramento de tanques de precisão cirúrgica e controle rigoroso de perdas.',
        image: '/images/fuel_check_bg.png',
        icon: Gauge,
        color: 'text-[#7c3aed]',
        features: [
            {
                title: 'Medição Laser',
                description: 'Monitoramento volumétrico de tanques com precisão de 0.01mm.',
                icon: Activity
            },
            {
                title: 'Alertas Inteligentes',
                description: 'Notificações imediatas sobre vazamentos ou variações térmicas.',
                icon: Bell
            },
            {
                title: 'Reconciliação LMC',
                description: 'Geração automática do Livro de Movimentação de Combustíveis.',
                icon: FileText
            },
            {
                title: 'Níveis Cloud',
                description: 'Acompanhe o nível de combustível de qualquer lugar via dashboard.',
                icon: Globe
            }
        ],
        specs: [
            { label: 'Precisão', value: '99.98% Volumétrica' },
            { label: 'Sensores', value: 'Inteligentes / Laser' },
            { label: 'Conformidade', value: 'INMETRO / CONAMA' },
            { label: 'Monitoramento', value: '24h Ativo' }
        ]
    },
    {
        id: 'ia',
        name: 'Lukos IA',
        description: 'Sua gestão na palma da mão. Consulte métricas, ticket médio e tire dúvidas técnicas via WhatsApp.',
        image: '/images/serviceia.png',
        icon: Brain,
        color: 'text-[#7c3aed]',
        features: [
            {
                title: 'Chatbot WhatsApp',
                description: 'Consulte vendas do dia e ticket médio enviando apenas uma mensagem.',
                icon: MessageCircle
            },
            {
                title: 'Análise Preditiva',
                description: 'Previsão de vendas baseada em histórico e tendências locais.',
                icon: TrendingUp
            },
            {
                title: 'Luk Bot',
                description: 'Treinado especificamente para responder dúvidas sobre o setor de postos.',
                icon: Cpu
            },
            {
                title: 'Comandos de Voz',
                description: 'Interaja com seu baco de dados através de mensagens de áudio.',
                icon: Mic
            }
        ],
        specs: [
            { label: 'Motor IA', value: 'Lukos Core Neural' },
            { label: 'Integração', value: 'WhatsApp Business API' },
            { label: 'Segurança', value: 'LGPD Compliant' },
            { label: 'Tempo Resposta', value: 'Instantâneo' }
        ]
    }
];

// Reusable Component for the image-inspired Card
const FeatureCard = ({ title, description, icon: Icon }: any) => (
    <motion.div 
        whileHover={{ y: -5, shadow: "0 25px 50px -12px rgba(124,58,237,0.15)" }}
        className="relative bg-white/70 backdrop-blur-md p-8 rounded-[2.2rem] border border-white shadow-sm transition-all group flex flex-col h-full"
    >
        <div className="flex items-start justify-between mb-7">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center text-[#7c3aed] group-hover:from-[#7c3aed] group-hover:to-[#6d28d9] group-hover:text-white group-hover:shadow-[0_8px_20px_rgba(124,58,237,0.3)] transition-all duration-500">
                <Icon className="w-5.5 h-5.5" />
            </div>
            <div className="w-10 h-10 bg-slate-900 rounded-full flex items-center justify-center text-white rotate-[-45deg] group-hover:rotate-0 transition-transform duration-500">
                <ArrowRight className="w-4 h-4" />
            </div>
        </div>
        <div className="space-y-3">
            <h4 className="text-xl font-outfit font-black text-slate-900 tracking-[-0.03em] leading-tight">{title}</h4>
            
            <div className="flex items-center gap-2 py-1">
                <div className="w-1.5 h-1.5 rounded-full bg-[#7c3aed] opacity-80" />
                <p className="text-[10px] font-outfit font-black text-[#7c3aed] uppercase tracking-[0.25em] opacity-80">
                    LUKOS CORE FEATURE
                </p>
            </div>

            <p className="text-[14.5px] text-slate-500 leading-[1.6] font-medium">
                {description}
            </p>
        </div>
    </motion.div>
);

const ServiceDetailView = ({ service, onBack, onContact }: any) => (
    <motion.div 
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="relative min-h-screen bg-[#F9FAFB] flex flex-col"
    >
        {/* Floating Back Button */}
        <motion.button
            onClick={onBack}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="fixed top-8 left-8 z-[110] group p-4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl text-white hover:bg-white hover:text-slate-900 transition-all duration-500"
        >
            <ArrowLeft className="w-6 h-6 group-hover:-translate-x-1 transition-transform" />
        </motion.button>

        {/* Header Hero Section */}
        <div className="relative w-full h-[380px] lg:h-[460px] overflow-hidden">
            <motion.img 
                initial={{ scale: 1.15 }}
                animate={{ scale: 1 }}
                src={service.image} 
                alt="" 
                className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#F9FAFB] via-[#F9FAFB]/40 to-black/40" />
            
            {/* Top Edge Rim Light for Hero */}
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent z-20" />

            <div className="absolute inset-0 container mx-auto px-6 flex flex-col justify-end pb-12">
                <motion.button 
                    onClick={onBack}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="group mb-7 flex items-center gap-3 text-slate-900 font-outfit font-black uppercase text-[10px] tracking-[0.3em] bg-white/90 backdrop-blur-md px-6 py-3 rounded-full w-fit shadow-xl border border-white hover:bg-[#7c3aed] hover:text-white transition-all"
                >
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    Voltar para Soluções
                </motion.button>
                
                <div className="space-y-3">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="flex items-center gap-3"
                    >
                        <div className="w-11 h-11 rounded-2xl bg-[#7c3aed] flex items-center justify-center shadow-[0_12px_24px_rgba(124,58,237,0.4)]">
                            <service.icon className="w-5.5 h-5.5 text-white" />
                        </div>
                        <span className="text-[10px] font-outfit font-black text-[#7c3aed] uppercase tracking-[0.4em] bg-white/70 backdrop-blur-sm px-5 py-2 rounded-full border border-white/50">
                            Ecossistema Lukos
                        </span>
                    </motion.div>
                    <motion.h2 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-5xl lg:text-[5.4rem] font-outfit font-black text-slate-900 tracking-[-0.045em] leading-[1.05] pb-2"
                    >
                        {service.name}
                    </motion.h2>
                </div>
            </div>
        </div>

        {/* Content Section */}
        <div className="container mx-auto px-6 py-20">
            <div className="max-w-7xl mx-auto space-y-20">
                {/* Statement & Description */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-12">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-2 h-2 rounded-full bg-[#7c3aed]" />
                            <h4 className="text-[10px] font-outfit font-black text-[#7c3aed] uppercase tracking-[0.4em]">Declaração de Valor</h4>
                        </div>
                        <p className="text-3xl lg:text-[3.2rem] text-slate-900 font-outfit font-black tracking-[-0.04em] leading-[1.12]">
                            {service.description}
                        </p>
                    </div>
                </div>

                {/* Technical Specifications Section */}
                {service.specs && (
                    <div>
                        <div className="flex items-center gap-3 mb-8">
                            <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                            <h4 className="text-[10px] font-outfit font-black text-slate-400 uppercase tracking-[0.4em]">Especificações Técnicas</h4>
                            <div className="flex-1 h-[0.5px] bg-gradient-to-r from-slate-200 to-transparent" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {service.specs.map((spec: any, i: number) => (
                                <motion.div 
                                    key={i}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 + (i * 0.1) }}
                                    className="bg-white/40 backdrop-blur-md p-6 rounded-2xl border border-white shadow-sm flex flex-col justify-center transition-all hover:bg-white/60"
                                >
                                    <span className="text-[9px] font-outfit font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5">{spec.label}</span>
                                    <span className="text-[14px] font-sans font-bold text-slate-900 tracking-tight">{spec.value}</span>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Features Grid */}
                <div>
                    <div className="flex items-center gap-3 mb-10">
                        <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                        <h4 className="text-[10px] font-outfit font-black text-slate-400 uppercase tracking-[0.4em]">Recursos & Inteligência</h4>
                        <div className="flex-1 h-[0.5px] bg-gradient-to-r from-slate-200 to-transparent" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {service.features?.map((feature: any, i: number) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 + (i * 0.1) }}
                            >
                                <FeatureCard {...feature} />
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Closing Action Card */}
                <div className="relative group p-1 lg:p-1.5 bg-gradient-to-r from-[#7c3aed] to-indigo-600 rounded-[3.5rem] overflow-hidden shadow-2xl">
                    <div className="bg-slate-900 p-12 lg:p-20 rounded-[3rem] flex flex-col lg:flex-row items-center justify-between gap-12 relative overflow-hidden">
                        {/* Abstract Glows */}
                        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/10 rounded-full blur-[100px] -mr-32 -mt-32" />
                        <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-[100px] -ml-32 -mb-32" />
                        
                        <div className="relative z-10 text-center lg:text-left space-y-6">
                            <h3 className="text-4xl lg:text-[3.2rem] font-outfit font-black text-white tracking-[-0.035em] leading-tight">
                                Pronto para elevar o <br />nível do seu posto?
                            </h3>
                            <p className="text-slate-400 text-lg font-medium max-w-lg">
                                Nossos especialistas estão prontos para desenhar uma solução sob medida para sua operação.
                            </p>
                        </div>
                        
                        <button 
                            onClick={onContact}
                            className="relative z-10 group bg-white text-slate-900 px-12 py-6 rounded-[2rem] font-outfit font-black text-xs uppercase tracking-[0.2em] shadow-2xl hover:bg-[#7c3aed] hover:text-white transition-all hover:scale-105 active:scale-95 flex items-center gap-4"
                        >
                            <MessageCircle className="w-5 h-5 transition-transform group-hover:scale-110" />
                            <span>Falar com Especialista</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </motion.div>
);

const ServiceCardGrid = () => {
    const [selectedService, setSelectedService] = useState<any>(null);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

    const openWhatsApp = (serviceName: string) => {
        const message = encodeURIComponent(`Olá, gostaria de saber mais sobre o serviço: ${serviceName}`);
        window.open(`https://wa.me/5511999999999?text=${message}`, '_blank');
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setMousePos({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });
    };

    return (
        <section className="bg-[#FCFCFD] overflow-hidden min-h-screen">
            <style dangerouslySetInnerHTML={{ __html: `
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
            `}} />

            <AnimatePresence mode="wait">
                {!selectedService ? (
                    <motion.div 
                        key="grid"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, x: -20 }}
                        className="py-24 container mx-auto px-6 max-w-7xl"
                    >
                        <div className="text-center mb-16">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                className="inline-flex items-center gap-4 mb-7"
                            >
                                <div className="inline-flex items-center gap-2.5 px-5 py-2 bg-slate-100 rounded-full text-[10px] font-outfit font-black text-slate-500 uppercase tracking-[0.3em] border border-white/50">
                                    <div className="w-1.5 h-1.5 rounded-full bg-[#7c3aed] animate-pulse" />
                                    Ecossistema Lukos
                                </div>
                                <div className="h-6 w-px bg-slate-200"></div>
                                <PoweredByLuk variant="light" />
                            </motion.div>
                            <motion.h2
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                className="text-5xl md:text-7xl font-outfit font-black text-slate-900 mb-8 tracking-[-0.04em] leading-tight"
                            >
                                Nossas Soluções
                            </motion.h2>
                            <motion.p
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.1 }}
                                className="text-xl text-slate-500/80 max-w-2xl mx-auto leading-[1.8] font-medium"
                            >
                                Tecnologia de ponta desenhada para simplificar cada detalhe da sua operação com inteligência real.
                            </motion.p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {services.map((service, index) => (
                                <motion.div
                                    key={service.id}
                                    initial={{ opacity: 0, scale: 0.98 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: index * 0.08 }}
                                    onMouseMove={handleMouseMove}
                                    onClick={() => setSelectedService(service)}
                                    style={{ perspective: '1200px' }}
                                    className="group relative flex flex-col h-full bg-white rounded-[2.8rem] p-4 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_30px_60px_rgba(124,58,237,0.12)] transition-all duration-500 cursor-pointer overflow-hidden"
                                >
                                    {/* Studio Light Effect on Card Top */}
                                    <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#7c3aed]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-20" />

                                    {/* 3D Content Wrapper */}
                                    <motion.div 
                                        whileHover={{ rotateY: -3, rotateX: 2 }}
                                        transition={{ type: "spring", stiffness: 200, damping: 25 }}
                                        className="relative h-full flex flex-col"
                                    >
                                        {/* Card Image Container */}
                                        <div className="relative mb-7 rounded-[2.2rem] overflow-hidden aspect-[4/3] bg-slate-100 shadow-inner">
                                            <img
                                                src={service.image}
                                                alt={service.name}
                                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 blur-0 group-hover:blur-[1px] opacity-100 group-hover:opacity-60"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-[#7c3aed]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                            
                                            {/* Centered Icon on Hover */}
                                            <div className="absolute inset-0 flex items-center justify-center scale-0 group-hover:scale-100 transition-transform duration-500 pointer-events-none">
                                                <div className="w-22 h-22 rounded-[2.5rem] bg-white shadow-[0_20px_40px_rgba(0,0,0,0.2)] flex items-center justify-center border border-white/50 backdrop-blur-sm">
                                                    <service.icon className="w-10 h-10 text-[#7c3aed]" />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Text Content */}
                                        <div className="px-6 pb-6 flex-1 flex flex-col justify-between">
                                            <div>
                                                <div className="flex items-center justify-between mb-3.5">
                                                    <h3 className="text-2xl lg:text-[2.2rem] font-outfit font-black text-slate-900 tracking-[-0.04em] leading-none mb-1">
                                                        {service.name}
                                                    </h3>
                                                    <div className={`p-2.5 rounded-2xl bg-slate-50 group-hover:bg-[#7c3aed] transition-all duration-300 shadow-sm group-hover:shadow-[0_8px_16px_rgba(124,58,237,0.3)]`}>
                                                        <service.icon className="w-5 h-5 text-slate-400 group-hover:text-white" />
                                                    </div>
                                                </div>
                                                
                                                <div className="flex items-center gap-2 mb-6">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-[#7c3aed] opacity-60" />
                                                    <span className="text-[10px] font-outfit font-bold text-slate-400 uppercase tracking-widest">Lukos Solutions</span>
                                                </div>

                                                <p className="text-slate-500 text-[15px] leading-relaxed mb-10 font-medium line-clamp-2">
                                                    {service.description}
                                                </p>
                                            </div>

                                            <div className="flex items-center justify-between pt-6 border-t border-slate-50">
                                                <span className="text-[10px] font-outfit font-black text-[#7c3aed] uppercase tracking-[0.2em] opacity-80">Ver Detalhes</span>
                                                <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center group-hover:bg-[#7c3aed] transition-all shadow-sm">
                                                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                ) : (
                    <ServiceDetailView 
                        key="detail"
                        service={selectedService} 
                        onBack={() => setSelectedService(null)}
                        onContact={() => openWhatsApp(selectedService.name)}
                    />
                )}
            </AnimatePresence>
        </section>
    );
};

export default ServiceCardGrid;
